import { useState } from 'react'

/**
 * @typedef {Object} UseStepperReturn
 * @property {number} currentStep - The current step.
 * @property {Function} nextStep - Move to the next step.
 * @property {Function} prevStep - Move to the previous step.
 * @property {Function} goToStep - Move to a specific step.
 * @property {Function} resetStepper - Reset the stepper to the initial step.
 * @property {boolean} isLastStep - Indicates if the current step is the last step.
 * @property {boolean} isFirstStep - Indicates if the current step is the first step.
 */

/**
 * Custom hook to control a stepper.
 *
 * @param {Object} params - The hook params.
 * @param {number} [params.steps=2] - The total number of steps.
 * @param {number} [params.initialStep=1] - The initial step to start from.
 * @returns {UseStepperReturn} The current step and functions to control the stepper.
 */

const useStepper = ({ initialStep = 1, steps = 2 }) => {
  const [currentStep, setCurrentStep] = useState(initialStep)

  /**
   * Advances to the next step in the stepper.
   */
  const nextStep = () => {
    setCurrentStep((prev) => (prev < steps ? prev + 1 : prev))
  }

  /**
   * Goes back one step.
   */
  const prevStep = () => {
    setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev))
  }

  /**
   * Moves to a specific step.
   *
   * @param {number} step - The step to go
   */
  const goToStep = (step) => {
    if (step >= 1 && step <= steps) {
      setCurrentStep(step)
    }
  }

  /**
   * Resets the stepper to the initial step.
   */
  const resetStepper = () => {
    setCurrentStep(initialStep)
  }

  return {
    currentStep,
    nextStep,
    prevStep,
    goToStep,
    resetStepper,
    isLastStep: currentStep === steps,
    isFirstStep: currentStep === 1,
  }
}

export default useStepper
